import { fetchCounts } from '@/atoms/stats'
import { Button } from '@/components/ui/button'
import { useSecondaryPage } from '@/PageManager'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'

import { toNote } from '@/lib/link'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { Event } from '@nostr/tools/wasm'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircle } from '@hugeicons/core-free-icons'
import Collapsible from './Collapsible'
import Content from './Content'
import PendingUndoButton from './PendingUndoButton'
import { FormattedTimestamp } from './FormattedTimestamp'
import Nip05 from './Nip05'
import NoteOptions from './NoteOptions'
import NoteStats from './NoteStats'
import ParentNotePreview from './ParentNotePreview'
import UserAvatar from './UserAvatar'
import Username from './Username'
import { MenuAction } from './NoteOptions/useMenuActions'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { getParents } from '@/lib/event'

export default function ReplyNote({
  event,
  onClickParent = () => {},
  highlight = false,
  simpleEmbedId,
  bulkReplyMode = false,
  isSelected = false,
  onToggleSelect,
  onBulkReply
}: {
  event: Event
  onClickParent?: (parent: EventPointer | AddressPointer) => void
  highlight?: boolean
  simpleEmbedId?: string
  bulkReplyMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  onBulkReply?: () => void
}) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [showMuted, setShowMuted] = useState(false)
  const show = useMemo(() => {
    if (showMuted) return true
    if (mutePubkeySet.has(event.pubkey)) return false
    return true
  }, [showMuted, mutePubkeySet, event])
  const parents = useMemo(() => getParents(event), [event])
  const extraActions: MenuAction[] = useMemo(() => {
    if (!onBulkReply) return []
    return [
      {
        icon: MessageCircle,
        label: t('Reply in bulk'),
        onClick: onBulkReply,
        separator: true
      } as MenuAction
    ]
  }, [onBulkReply, t])

  const containerRef = useRef<HTMLDivElement>(null)
  const hasFetched = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFetched.current) {
          timerRef.current = setTimeout(() => {
            hasFetched.current = true
            fetchCounts(event)
            observer.unobserve(el)
          }, 2000)
        } else if (!entry.isIntersecting && timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = undefined
        }
      },
      { threshold: 0 }
    )

    observer.observe(el)
    return () => {
      observer.disconnect()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [event.id])

  return (
    <div
      ref={containerRef}
      className={`pb-1 border-b transition-colors duration-500 ${highlight ? 'bg-primary/50' : ''}`}
      onClick={() => {
        if (bulkReplyMode) {
          onToggleSelect?.()
        }
      }}
    >
      <Collapsible>
        <div className="flex space-x-2 items-start px-4 pt-3">
          {bulkReplyMode && (
            <div className="shrink-0 mt-1.5" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect?.()}
                className="size-5"
              />
            </div>
          )}
          <UserAvatar user={event.pubkey} size="medium" className="shrink-0 mt-0.5" />
          <div className="w-full overflow-hidden">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 w-0">
                <div className="flex gap-1 items-center">
                  <Username
                    user={event.pubkey}
                    className="text-sm font-semibold text-muted-foreground hover:text-foreground truncate"
                    skeletonClassName="h-3"
                  />
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Nip05 user={event.pubkey} append="·" />
                  <FormattedTimestamp
                    timestamp={event.created_at}
                    className="shrink-0"
                    short={isSmallScreen}
                    href={toNote(event)}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      push(toNote(event))
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center shrink-0">
                <NoteOptions
                  event={event}
                  className="shrink-0 [&_svg]:size-5"
                  extraActions={extraActions}
                />
              </div>
            </div>
            <PendingUndoButton event={event} className="mt-1" />
            <div
              className="grid gap-2 shrink-0"
              style={{ gridTemplateColumns: `repeat(${parents.length}, minmax(0, 1fr))` }}
            >
              {parents.map((pointer, i) => (
                <ParentNotePreview
                  key={
                    'value' in pointer
                      ? pointer.value
                      : 'id' in pointer
                        ? pointer.id
                        : pointer.identifier
                  }
                  first={i === 0}
                  parent={pointer}
                  className="mt-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    if ('value' in pointer) {
                      /* push(toNoteList({ external: pointer })) */
                    } else onClickParent(pointer)
                  }}
                />
              ))}
            </div>
            {show ? (
              <Content className="mt-2" event={event} simpleEmbedId={simpleEmbedId} />
            ) : (
              <Button
                variant="outline"
                className="text-muted-foreground font-medium mt-2"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMuted(true)
                }}
              >
                {t('Temporarily display this reply')}
              </Button>
            )}
          </div>
        </div>
      </Collapsible>
      {show && <NoteStats className="ml-14 pl-1 mr-4 mt-2" event={event} />}
    </div>
  )
}

export function ReplyNoteSkeleton() {
  return (
    <div className="px-4 py-3 flex items-start space-x-2 w-full">
      <Skeleton className="w-9 h-9 rounded-full shrink-0 mt-0.5" />
      <div className="w-full">
        <div className="py-1">
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="my-1">
          <Skeleton className="w-full h-4 my-1 mt-2" />
        </div>
        <div className="my-1">
          <Skeleton className="w-2/3 h-4 my-1" />
        </div>
      </div>
    </div>
  )
}
